import React, { useCallback, useEffect, useState } from 'react';
import {
    Box, Typography, Paper, CircularProgress, Alert, TextField, Button,
    Table, TableHead, TableRow, TableCell, TableBody, Tooltip, Stack
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PeopleIcon from '@mui/icons-material/People';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import PercentIcon from '@mui/icons-material/Percent';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { BioService } from '../../../../core/services/bio.service';
import {
    ResponsiveContainer,
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
} from "recharts";
import RechartsNumberTooltip from '../../../../components/tailwind-component/ui/rechartsNumberTooltip';

interface Props { influencer_id: number; account_id: number; }

// const StatCard = ({ icon, label, value, color }: { icon: React.ReactNode; label: string; value: any; color: string }) => (
//     <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, flex: 1, minWidth: 160 }}>
//         <Box display="flex" alignItems="center" gap={1} mb={0.5} sx={{ color }}>{icon}
//             <Typography variant="caption" color="text.secondary">{label}</Typography>
//         </Box>
//         <Typography variant="h5" fontWeight={700}>{value}</Typography>
//     </Paper>
// );
const StatCard = ({
    icon,
    label,
    value,
    color,
    subtitle,
}: {
    icon: React.ReactNode;
    label: string;
    value: any;
    color: string;
    subtitle?: string;
}) => (
    <Paper
        elevation={0}
        sx={{
            flex: 1,
            minWidth: 220,
            borderRadius: "16px",
            border: "1px solid #E5E7EB",
            p: 2.5,
            transition: ".25s",
            cursor: "default",

            "&:hover": {
                transform: "translateY(-4px)",
                boxShadow: "0 14px 35px rgba(0,0,0,.08)",
            },
        }}
    >
        <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
        >
            <Box>
                <Typography
                    variant="body2"
                    color="text.secondary"
                >
                    {label}
                </Typography>

                <Typography
                    sx={{
                        fontSize: 'clamp(30px, 4vw, 48px)',
                    }}
                    fontWeight={800}
                    mt={1}
                >
                    {value}
                </Typography>

                {subtitle && (
                    <Typography
                        variant="caption"
                        color="text.secondary"
                    >
                        {subtitle}
                    </Typography>
                )}
            </Box>

            <Box
                sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 3,
                    bgcolor: `${color}15`,
                    color,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                {icon}
            </Box>
        </Box>
    </Paper>
);

export const BioAnalytics: React.FC<Props> = ({ influencer_id, account_id }) => {
    const [loading, setLoading] = useState(false);
    const [summary, setSummary] = useState<any>({});
    const [perBlock, setPerBlock] = useState<any[]>([]);
    const [trend, setTrend] = useState<any[]>([]);
    // Draft values bound to the two date inputs. Editing these must NOT refetch —
    // OLD behaviour: `load` depended on them and `useEffect(..., [load])` re-ran on
    // every pick, so the filter auto-applied before "Apply Filters".
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    // Committed values — only these drive the query, and only "Apply Filters" moves
    // the drafts into them.
    const [appliedStart, setAppliedStart] = useState('');
    const [appliedEnd, setAppliedEnd] = useState('');
    const [alert, setAlert] = useState<string | null>(null);

    const load = useCallback(() => {
        if (!influencer_id || !account_id) return;
        setLoading(true);
        BioService.getAnalytics({ influencer_id, account_id, start_date: appliedStart, end_date: appliedEnd })
            .then((res: any) => {
                if (res?.status === 1 && res?.data?.rows) {
                    setSummary(res.data.rows.summary || {});
                    setPerBlock(res.data.rows.per_block || []);
                    setTrend(res.data.rows.trend || []);
                } else { setSummary({}); setPerBlock([]); setTrend([]); }
            })
            .catch(() => setAlert('Failed to load analytics'))
            .finally(() => setLoading(false));
    }, [influencer_id, account_id, appliedStart, appliedEnd]);

    useEffect(() => { load(); }, [load]);

    // Today, as YYYY-MM-DD, for the inputs' `max` so future dates can't be picked.
    const todayStr = new Date().toLocaleDateString('en-CA');

    // Either bound may be used on its own:
    //   From only -> that date onwards;  To only -> everything up to that date.
    // The only invalid combination is an inverted range.
    const dateError =
        startDate && endDate && startDate > endDate ? '"From" cannot be after "To".'
        : (startDate > todayStr || endDate > todayStr) ? 'Future dates are not allowed.'
        : '';

    // Commits the drafts; `load` then refetches because appliedStart/appliedEnd changed.
    const applyFilters = () => {
        if (dateError) return;
        setAppliedStart(startDate);
        setAppliedEnd(endDate);
    };

    const filtersActive = !!startDate || !!endDate || !!appliedStart || !!appliedEnd;

    // A Affiliate Deals cross-post's url is the raw Trackier tracking_url (needed
    // server-side to resolve /bio/r/:click_token) — never surface that here, same
    // as the Storefront analytics/product list and Affiliate Deals itself.
    const displayLink = (b: any) =>
        b.source_type === 'cashkaro' && b.click_token ? BioService.clickUrl(b.click_token) : b.url;

    const clearFilters = () => {
        setStartDate(''); setEndDate('');
        setAppliedStart(''); setAppliedEnd('');
    };

    const maxViews = Math.max(1, ...trend.map(t => Number(t.views) || 0));
    const chartData = trend.map((item) => ({
        date: item.date,
        Views: Number(item.views || 0),
        Visitors: Number(item.visitors || 0),
    }));
    return (
        <Box>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: { xs: "flex-start", lg: "center" },
                    flexDirection: { xs: "column", lg: "row" },
                    gap: 3,
                    mb: 4,
                }}
            >
                {/* Left */}
                <Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="h5" fontWeight={700}>
                            Bio Analytics
                        </Typography>
                        <Tooltip title="Monitor your Link in Bio page performance, visitor activity and link engagement.">
                            <InfoOutlined sx={{ fontSize: 18, color: "text.secondary", cursor: "pointer" }} />
                        </Tooltip>
                    </Stack>
                    <Typography color="text.secondary">
                        Monitor traffic, visitors and link engagement across your page.
                    </Typography>
                </Box>

                {/* Right */}
                <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ width: { xs: "100%", sm: "auto" } }}>
                    <TextField
                        size="small"
                        type="date"
                        label="From"
                        InputLabelProps={{ shrink: true }}
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        inputProps={{ max: endDate && endDate < todayStr ? endDate : todayStr }}
                        error={!!dateError}
                        sx={{ flex: { xs: 1, sm: "none" }, minWidth: 120 }}
                    />
                    <TextField
                        size="small"
                        type="date"
                        label="To"
                        InputLabelProps={{ shrink: true }}
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        inputProps={{ min: startDate || undefined, max: todayStr }}
                        error={!!dateError}
                        sx={{ flex: { xs: 1, sm: "none" }, minWidth: 120 }}
                    />
                    <Button
                        variant="contained"
                        onClick={applyFilters}
                        disabled={!!dateError}
                        className='btn btn-primary'
                        // sx={{
                        //     bgcolor: "#000",
                        //     borderRadius: "10px",
                        //     px: 3,
                        //     height: 40,
                        //     textTransform: "none",
                        //     fontWeight: 600,
                        //     whiteSpace: "nowrap",
                        //     "&:hover": { bgcolor: "#111" },
                        // }}
                    >
                        Apply Filters
                    </Button>

                    {filtersActive && (
                        <Button
                            variant="text"
                            onClick={clearFilters}
                            sx={{ textTransform: "none", fontWeight: 600, whiteSpace: "nowrap", color: "#6B7280", height: 40 }}
                        >
                            Clear
                        </Button>
                    )}

                    {dateError && (
                        <Typography variant="caption" color="error" sx={{ fontWeight: 500, width: "100%" }}>
                            {dateError}
                        </Typography>
                    )}
                </Stack>
            </Box>

            {alert && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setAlert(null)}>{alert}</Alert>}

            {loading ? (
                <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
            ) : (
                <>
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "1fr 1fr",
                                sm: "1fr 1fr",
                                lg: "repeat(4,1fr)",
                            },
                            gap: { xs: 1.5, md: 3 },
                            mb: 4,
                        }}
                    >
                        <StatCard icon={<VisibilityIcon fontSize="small" />} label="Page views" value={summary.total_views ?? 0} color="#2563EB" />
                        <StatCard icon={<PeopleIcon fontSize="small" />} label="Unique visitors" value={summary.unique_visitors ?? 0} color="#7C3AED" />
                        <StatCard icon={<TouchAppIcon fontSize="small" />} label="Link clicks" value={summary.total_clicks ?? 0} color="#16A34A" />
                        <StatCard icon={<PercentIcon fontSize="small" />} label="Click-through rate" value={`${summary.ctr ?? 0}%`} color="#D97706" />
                    </Box>

                    {/* <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                        <Typography variant="subtitle2" fontWeight={700} mb={1.5}>Views trend</Typography>
                        {trend.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">No views in this period yet.</Typography>
                        ) : (
                            <Box display="flex" alignItems="flex-end" gap={0.5} sx={{ height: 120, overflowX: 'auto' }}>
                                {trend.map((t, i) => (
                                    <Tooltip key={i} title={`${t.date}: ${t.views} views, ${t.visitors} visitors`}>
                                        <Box sx={{ width: 14, flexShrink: 0, height: `${Math.max(4, (Number(t.views) / maxViews) * 110)}px`, bgcolor: '#2563EB', borderRadius: '3px 3px 0 0', opacity: 0.85 }} />
                                    </Tooltip>
                                ))}
                            </Box>
                        )}
                    </Paper> */}
                    <Paper
                        elevation={0}
                        sx={{
                            borderRadius: "16px",
                            border: "1px solid #E5E7EB",
                            overflow: "hidden",
                            mb: 4,
                        }}
                    >
                        <Box
                            sx={{
                                px: 3,
                                py: 2,
                                borderBottom: "1px solid #F1F5F9",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                            }}
                        >
                            <Box>
                                <Typography
                                    variant="h6"
                                    fontWeight={700}
                                >
                                    Views Trend
                                </Typography>

                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    Daily traffic to your Bio page
                                </Typography>
                            </Box>

                            <Box
                                sx={{
                                    px: 2,
                                    py: .5,
                                    borderRadius: 10,
                                    bgcolor: "#EEF2FF",
                                    color: "#4F46E5",
                                    fontWeight: 700,
                                    fontSize: 13,
                                }}
                            >
                                {chartData.length} Days
                            </Box>
                        </Box>

                        {chartData.length === 0 ? (
                            <Box
                                sx={{
                                    py: 8,
                                    textAlign: "center",
                                }}
                            >
                                <VisibilityIcon
                                    sx={{
                                        fontSize: 60,
                                        color: "#CBD5E1",
                                        mb: 1,
                                    }}
                                />

                                <Typography
                                    fontWeight={700}
                                >
                                    No Analytics Yet
                                </Typography>

                                <Typography
                                    color="text.secondary"
                                    mt={1}
                                >
                                    Your page views will appear here once visitors
                                    start visiting your bio page.
                                </Typography>
                            </Box>
                        ) : (
                            <Box sx={{ height: 360, p: 2 }}>
                                <ResponsiveContainer
                                    width="100%"
                                    height="100%"
                                >
                                    <AreaChart data={chartData}>
                                        <defs>
                                            <linearGradient
                                                id="viewsGradient"
                                                x1="0"
                                                y1="0"
                                                x2="0"
                                                y2="1"
                                            >
                                                <stop
                                                    offset="5%"
                                                    stopColor="#2563EB"
                                                    stopOpacity={0.4}
                                                />
                                                <stop
                                                    offset="95%"
                                                    stopColor="#2563EB"
                                                    stopOpacity={0}
                                                />
                                            </linearGradient>
                                        </defs>

                                        <CartesianGrid
                                            strokeDasharray="4 4"
                                            vertical={false}
                                        />

                                        <XAxis
                                            dataKey="date"
                                            tick={{ fontSize: 12 }}
                                        />

                                        <YAxis />

                                        <RechartsNumberTooltip />

                                        <Area
                                            type="monotone"
                                            dataKey="Views"
                                            stroke="#2563EB"
                                            strokeWidth={3}
                                            fill="url(#viewsGradient)"
                                        />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </Box>
                        )}
                    </Paper>

                    {/* <Typography variant="subtitle2" fontWeight={700} mb={1}>Top links by clicks</Typography>
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Link</TableCell>
                                    <TableCell>Destination</TableCell>
                                    <TableCell align="right">Clicks</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {perBlock.length === 0 ? (
                                    <TableRow><TableCell colSpan={3} align="center" sx={{ py: 3, color: 'text.secondary' }}>No links yet.</TableCell></TableRow>
                                ) : perBlock.map((b) => (
                                    <TableRow key={b.block_id} hover>
                                        <TableCell><Typography variant="body2" noWrap sx={{ maxWidth: 280 }} title={b.title}>{b.title || '—'}</Typography></TableCell>
                                        <TableCell sx={{ maxWidth: 280 }}><Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }} title={b.url}>{b.url}</Typography></TableCell>
                                        <TableCell align="right"><Typography variant="body2" fontWeight={700}>{b.clicks ?? 0}</Typography></TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer> */}
                    <Paper
                        elevation={0}
                        sx={{
                            borderRadius: "16px",
                            border: "1px solid #E5E7EB",
                            overflow: "hidden",
                        }}
                    >
                        {/* Header */}
                        <Box
                            sx={{
                                px: 3,
                                py: 2,
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                borderBottom: "1px solid #EEF2F7",
                            }}
                        >
                            <Box>
                                <Typography
                                    variant="h6"
                                    fontWeight={700}
                                >
                                    Top Performing Links
                                </Typography>

                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    Ranked by total clicks
                                </Typography>
                            </Box>

                            <Box
                                sx={{
                                    px: 2,
                                    py: .5,
                                    bgcolor: "#EEF2FF",
                                    color: "#4F46E5",
                                    borderRadius: 10,
                                    fontWeight: 700,
                                    fontSize: 13,
                                }}
                            >
                                {perBlock.length} Links
                            </Box>
                        </Box>

                        {perBlock.length === 0 ? (
                            <Box
                                sx={{
                                    py: 8,
                                    textAlign: "center",
                                }}
                            >
                                <TouchAppIcon
                                    sx={{
                                        fontSize: 60,
                                        color: "#CBD5E1",
                                        mb: 1,
                                    }}
                                />

                                <Typography fontWeight={700}>
                                    No Link Clicks Yet
                                </Typography>

                                <Typography
                                    color="text.secondary"
                                    mt={1}
                                >
                                    Once visitors click your links,
                                    they'll appear here.
                                </Typography>
                            </Box>
                        ) : (
                            <>
                                {/* Desktop / tablet: table (horizontally scrollable on tablet) */}
                                <Box sx={{ display: { xs: 'none', sm: 'block' }, overflowX: 'auto' }}>
                                    <Table>
                                        <TableHead>
                                            <TableRow
                                                sx={{
                                                    bgcolor: "#F8FAFC",
                                                }}
                                            >
                                                <TableCell>#</TableCell>

                                                <TableCell>
                                                    Link
                                                </TableCell>

                                                <TableCell>
                                                    Destination
                                                </TableCell>

                                                <TableCell align="right">
                                                    Clicks
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>

                                        <TableBody>

                                            {perBlock.map((item, index) => (

                                                <TableRow
                                                    key={item.block_id}
                                                    hover
                                                    sx={{
                                                        transition: ".25s",

                                                        "&:hover": {
                                                            bgcolor: "#F8FAFF",
                                                        },
                                                    }}
                                                >

                                                    {/* Rank */}

                                                    <TableCell width={70}>

                                                        <Box
                                                            sx={{
                                                                width: 34,
                                                                height: 34,
                                                                borderRadius: "50%",
                                                                bgcolor: "#EEF2FF",
                                                                color: "#4F46E5",
                                                                fontWeight: 700,
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                            }}
                                                        >
                                                            {index + 1}
                                                        </Box>

                                                    </TableCell>

                                                    {/* Title */}

                                                    <TableCell>

                                                        <Typography
                                                            fontWeight={700}
                                                        >
                                                            {item.title || "Untitled"}
                                                        </Typography>

                                                    </TableCell>

                                                    {/* URL */}

                                                    <TableCell>

                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                            noWrap
                                                            sx={{
                                                                maxWidth: 300,
                                                            }}
                                                        >
                                                            {displayLink(item)}
                                                        </Typography>

                                                    </TableCell>

                                                    {/* Clicks */}

                                                    <TableCell align="right">

                                                        <Box
                                                            sx={{
                                                                display: "inline-flex",
                                                                px: 2,
                                                                py: .6,
                                                                borderRadius: 10,
                                                                bgcolor: "#DCFCE7",
                                                                color: "#15803D",
                                                                fontWeight: 700,
                                                            }}
                                                        >
                                                            {item.clicks ?? 0}
                                                        </Box>

                                                    </TableCell>

                                                </TableRow>

                                            ))}

                                        </TableBody>

                                    </Table>
                                </Box>

                                {/* Mobile: stacked cards instead of a table */}
                                <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                                    {perBlock.map((item, index) => (
                                        <Box
                                            key={item.block_id}
                                            sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5, borderBottom: '1px solid #F1F5F9', '&:last-of-type': { borderBottom: 0 } }}
                                        >
                                            <Box sx={{
                                                width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
                                                bgcolor: '#EEF2FF', color: '#4F46E5', fontWeight: 700,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            }}>
                                                {index + 1}
                                            </Box>
                                            <Box flex={1} minWidth={0}>
                                                <Typography fontWeight={700} noWrap>{item.title || 'Untitled'}</Typography>
                                                <Typography variant="body2" color="text.secondary" noWrap>{displayLink(item)}</Typography>
                                            </Box>
                                            <Box sx={{
                                                flexShrink: 0, px: 1.5, py: 0.5, borderRadius: 10,
                                                bgcolor: '#DCFCE7', color: '#15803D', fontWeight: 700,
                                            }}>
                                                {item.clicks ?? 0}
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            </>
                        )}
                    </Paper>
                </>
            )}
        </Box>
    );
};
